import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import {
  useCancelOwnPack,
  useConfirmStripeSession,
  useDownloadPackInvoice,
  useLeadsVoAgent,
  useLeadsVoPacks,
  useOrderPack,
  useResumePackPayment,
} from '../../hooks/useLeadsVoQueries';
import { LeadsVoBankTransferInfo, LeadsVoPack, LeadsVoPaymentMethod } from '../../types/leadsVo';

// Cache des instances Stripe par clé publishable (loadStripe ne doit être appelé qu'une fois par clé).
const stripePromiseCache: Record<string, Promise<Stripe | null>> = {};
const getStripePromise = (pk: string) => {
  if (!stripePromiseCache[pk]) stripePromiseCache[pk] = loadStripe(pk);
  return stripePromiseCache[pk];
};

// Moyens de paiement qu'une agence peut choisir : un pack « offert » n'est créé que par un admin.
type OrderablePaymentMethod = Exclude<LeadsVoPaymentMethod, 'offered'>;

const TRIAL_SIZE = 5;
const REGULAR_SIZES = [10, 20, 50];
const UNIT_PRICE_HT = 35;

const PACK_STATUS: Record<string, { label: string; bg: string; color: string }> = {
  pending_payment: { label: 'Paiement en attente', bg: '#FFF4E0', color: '#946200' },
  paid: { label: 'Payé', bg: '#E8F5E9', color: '#2E7D32' },
  active: { label: 'Actif', bg: '#E8F5E9', color: '#2E7D32' },
  closed: { label: 'Terminé', bg: '#F5F5F5', color: '#616161' },
  cancelled: { label: 'Annulé', bg: '#FDECEA', color: '#B71C1C' },
};

// Bloc RIB réutilisable avec boutons copier (UX : agence colle dans son interface bancaire).
const BankTransferDetails = ({ info, amountTtc }: { info: LeadsVoBankTransferInfo; amountTtc?: number | null }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };
  const Row = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Typography sx={{ fontSize: 12, color: '#666', minWidth: 110, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: '#262626', flex: 1, wordBreak: 'break-all' }}>
        {value}
      </Typography>
      <Button
        size="small"
        onClick={() => copy(label, value)}
        sx={{ minWidth: 'auto', textTransform: 'none', color: copied === label ? '#2E7D32' : '#FDBB5F', fontSize: 12, fontWeight: 700 }}
      >
        {copied === label ? '✓ Copié' : 'Copier'}
      </Button>
    </Box>
  );
  return (
    <Box sx={{ backgroundColor: '#fff', border: '1px solid #EEE', borderRadius: 1, p: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#262626', mb: 1.5 }}>
        Coordonnées bancaires Skipcar
      </Typography>
      <Row label="Bénéficiaire" value={info.account_holder} />
      <Row label="IBAN" value={info.iban} />
      {info.bic && <Row label="BIC" value={info.bic} />}
      {info.reference && <Row label="Libellé" value={info.reference} />}
      {amountTtc != null && <Row label="Montant" value={`${amountTtc}€ TTC`} />}
      <Typography sx={{ fontSize: 11, color: '#9E9E9E', mt: 1 }}>
        Indiquez le libellé pour faciliter le rapprochement du paiement.
      </Typography>
    </Box>
  );
};

// Bouton de téléchargement de la facture Qonto du pack (virement ou carte).
const DownloadInvoiceButton = ({ pack }: { pack: LeadsVoPack }) => {
  const downloadInvoice = useDownloadPackInvoice();
  const isNotReady = (downloadInvoice.error as { response?: { status?: number } } | null)?.response?.status === 409;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="outlined"
        disabled={downloadInvoice.isPending}
        onClick={() => downloadInvoice.mutate(pack)}
        sx={{ textTransform: 'none', fontWeight: 600, color: '#262626', borderColor: '#FDBB5F' }}
      >
        {downloadInvoice.isPending
          ? 'Téléchargement…'
          : `Télécharger la facture${pack.qonto_invoice_number ? ` n° ${pack.qonto_invoice_number}` : ''}`}
      </Button>
      {downloadInvoice.isError && (
        <Typography sx={{ fontSize: 12, color: '#B71C1C', mt: 0.5 }}>
          {isNotReady
            ? 'La facture est en cours de génération, réessayez dans quelques minutes.'
            : 'Impossible de télécharger la facture. Réessayez ou contactez Skipcar.'}
        </Typography>
      )}
    </Box>
  );
};

const PackCardComponent = ({
  pack,
  onResume,
  onCancel,
  busy,
}: {
  pack: LeadsVoPack;
  onResume: (pack: LeadsVoPack) => void;
  onCancel: (pack: LeadsVoPack) => void;
  busy: boolean;
}) => {
  const progress = pack.size > 0 ? (pack.rdv_consumed / pack.size) * 100 : 0;
  const status = PACK_STATUS[pack.status] ?? { label: pack.status, bg: '#F5F5F5', color: '#616161' };
  const isPending = pack.status === 'pending_payment';
  const hasInvoice = !!pack.qonto_invoice_id;

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, borderLeft: '4px solid #FDBB5F' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>
          {pack.reference ?? `Pack de ${pack.size} RDV`}
        </Typography>
        <Chip label={status.label} size="small" sx={{ backgroundColor: status.bg, color: status.color, fontWeight: 600 }} />
      </Box>

      {isPending ? (
        <Box sx={{ backgroundColor: '#FFF4E0', borderRadius: 1, p: 2 }}>
          {pack.payment_method === 'qonto_transfer' ? (
            <>
              <Typography sx={{ fontSize: 14, color: '#946200', mb: 1.5 }}>
                Facture {pack.qonto_invoice_number ? `n° ${pack.qonto_invoice_number} ` : ''}émise. Réglez par
                virement aux coordonnées ci-dessous — le pack sera activé dès réception du paiement.
              </Typography>
              {pack.bank_transfer && <BankTransferDetails info={pack.bank_transfer} amountTtc={pack.amount_ttc} />}
              {hasInvoice && <DownloadInvoiceButton pack={pack} />}
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: '#946200' }}>
              Paiement par carte non finalisé ({pack.amount_ttc ?? '—'}€ TTC).
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {pack.payment_method === 'stripe_card' && (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => onResume(pack)}
                sx={{ backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 700, '&:hover': { backgroundColor: '#fcae4d' } }}
              >
                Reprendre le paiement
              </Button>
            )}
            <Button size="small" color="error" variant="outlined" disabled={busy} onClick={() => onCancel(pack)}>
              Annuler
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" sx={{ color: '#464646', fontWeight: 500 }}>
                {pack.rdv_consumed} / {pack.size} RDV consommés
              </Typography>
              <Typography variant="body2" sx={{ color: '#464646', fontWeight: 600 }}>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#F0F0F0',
                '& .MuiLinearProgress-bar': { backgroundColor: '#FDBB5F', borderRadius: 5 },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#464646', fontSize: 13 }}>
            {pack.payment_method === 'offered'
              ? 'Offert par Skipcar'
              : `${pack.unit_price_ht}€ HT / RDV — Total : ${(pack.size * pack.unit_price_ht).toLocaleString('fr-FR')}€ HT`}
          </Typography>
          {hasInvoice && <DownloadInvoiceButton pack={pack} />}
        </>
      )}
    </Paper>
  );
};

const Packs = () => {
  const { data: agentData, isLoading: agentLoading } = useLeadsVoAgent();
  const { data: packs, isLoading } = useLeadsVoPacks();
  const orderPack = useOrderPack();

  const confirmStripe = useConfirmStripeSession();
  const resumePayment = useResumePackPayment();
  const cancelPack = useCancelOwnPack();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState<number>(10);
  const [customSize, setCustomSize] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<OrderablePaymentMethod>('qonto_transfer');
  const [error, setError] = useState('');
  const [qontoConfirmation, setQontoConfirmation] = useState<{
    invoiceNumber: string | null;
    bankTransfer?: LeadsVoBankTransferInfo;
    amountTtc?: number | null;
  } | null>(null);
  // Embedded Checkout Stripe (formulaire dans la page).
  const [embed, setEmbed] = useState<{ clientSecret: string; stripe: Promise<Stripe | null> } | null>(null);
  const [paymentResult, setPaymentResult] = useState<'success' | 'failed' | null>(null);

  // Stripe livre le résultat du paiement via le callback onComplete (Embedded Checkout
  // ne fait PAS de redirection). On extrait le session_id du client_secret pour confirmer
  // côté backend ; le format est `cs_test_xxxx_secret_yyyy` ou `cs_live_xxxx_secret_yyyy`.
  const handleEmbedComplete = async () => {
    if (!embed) return;
    const sessionId = embed.clientSecret.split('_secret_')[0];
    setEmbed(null);
    try {
      const r = await confirmStripe.mutateAsync(sessionId);
      setPaymentResult(r.paid ? 'success' : 'failed');
    } catch {
      setPaymentResult('failed');
    }
  };

  // Retour de l'Embedded Checkout : ?stripe_session=... -> confirmer le paiement.
  const stripeSession = searchParams.get('stripe_session');
  useEffect(() => {
    if (!stripeSession) return;
    (async () => {
      try {
        const r = await confirmStripe.mutateAsync(stripeSession);
        setPaymentResult(r.paid ? 'success' : 'failed');
      } catch {
        setPaymentResult('failed');
      } finally {
        searchParams.delete('stripe_session');
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeSession]);

  if (!agentLoading && agentData?.partner?.statut !== 'active') {
    return <Navigate to="/leads-vo/onboarding" replace />;
  }

  // Pack d'essai (5 RDV) : seulement si aucun pack non annulé, et par carte uniquement.
  const canOrderTrial = Array.isArray(packs) && packs.every((p) => p.status === 'cancelled');
  const availableSizes = canOrderTrial ? [TRIAL_SIZE, ...REGULAR_SIZES] : REGULAR_SIZES;

  const displaySize = customSize ? Number(customSize) : selectedSize;
  const isTrial = displaySize === TRIAL_SIZE;
  const totalHt = displaySize * UNIT_PRICE_HT;
  const totalTtc = Math.round(totalHt * 1.2 * 100) / 100;
  const effectivePaymentMethod: OrderablePaymentMethod = isTrial ? 'stripe_card' : paymentMethod;

  const resetDialog = () => {
    setDialogOpen(false);
    setError('');
    setCustomSize('');
    setSelectedSize(10);
    setPaymentMethod('qonto_transfer');
  };

  const handleOrder = async () => {
    const size = customSize ? Number(customSize) : selectedSize;
    if (size === TRIAL_SIZE) {
      if (!canOrderTrial) {
        setError('Le pack de 5 RDV est réservé à une première commande. Minimum 10 RDV.');
        return;
      }
    } else if (!size || size < 10) {
      setError('Le pack doit contenir au moins 10 RDV.');
      return;
    }
    try {
      setError('');
      const result = await orderPack.mutateAsync({ size, payment_method: effectivePaymentMethod });
      if (result.payment.method === 'stripe_card' && result.payment.client_secret && result.payment.publishable_key) {
        // Ouvre le formulaire de paiement EMBARQUÉ (pas de redirection vers Stripe).
        setEmbed({
          clientSecret: result.payment.client_secret,
          stripe: getStripePromise(result.payment.publishable_key),
        });
        setDialogOpen(false);
        return;
      }
      resetDialog();
      setQontoConfirmation({
        invoiceNumber: result.payment.invoice_number ?? null,
        bankTransfer: result.payment.bank_transfer,
        amountTtc: result.pack?.amount_ttc ?? null,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la commande');
    }
  };

  // Reprendre le paiement CB d'un pack en attente : rouvre le formulaire embarqué.
  const handleResume = async (pack: LeadsVoPack) => {
    try {
      const r = await resumePayment.mutateAsync(pack.id);
      setEmbed({ clientSecret: r.client_secret, stripe: getStripePromise(r.publishable_key) });
    } catch {
      setPaymentResult('failed');
    }
  };

  const handleCancel = async (pack: LeadsVoPack) => {
    await cancelPack.mutateAsync(pack.id);
  };

  // On masque les packs annulés de la liste (mais ils servent au calcul du pack d'essai).
  const allPacks: LeadsVoPack[] = packs ?? [];
  const visiblePacks = allPacks.filter((p) => p.status !== 'cancelled');
  const mutationBusy = resumePayment.isPending || cancelPack.isPending;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 22, sm: 28 }, color: '#262626' }}>Mes Packs de RDV</Typography>
          <Typography sx={{ color: '#666', fontSize: { xs: 13, sm: 15 } }}>
            Achetez des packs de rendez-vous qualifiés ({UNIT_PRICE_HT}€ HT / RDV)
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{
            backgroundColor: '#FDBB5F',
            color: '#262626',
            fontWeight: 700,
            py: { xs: 1.5, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: '#fcae4d' },
          }}
        >
          + Commander un pack
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: '#FDBB5F' }} />
        </Box>
      ) : visiblePacks.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 6, backgroundColor: '#FAFAFA' }}>
          <Typography sx={{ fontSize: 48, mb: 1 }}>📅</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626', mb: 1 }}>Aucun pack pour le moment</Typography>
          <Typography sx={{ color: '#666' }}>
            Commandez votre premier pack pour commencer à recevoir des RDV qualifiés.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {visiblePacks.map((pack) => (
            <Grid item xs={12} sm={6} key={pack.id}>
              <PackCardComponent pack={pack} onResume={handleResume} onCancel={handleCancel} busy={mutationBusy} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog commande */}
      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>Commander un pack de RDV</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666', fontSize: 14, mb: 2 }}>
            Sélectionnez la taille de votre pack ({UNIT_PRICE_HT}€ HT par RDV).
            {canOrderTrial && ' Profitez d’un pack d’essai de 5 RDV pour votre première commande (par carte).'}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {availableSizes.map((size) => (
              <Grid item xs={6} sm={3} key={size}>
                <Card
                  onClick={() => {
                    setSelectedSize(size);
                    setCustomSize('');
                  }}
                  sx={{
                    cursor: 'pointer',
                    border: selectedSize === size && !customSize ? '2px solid #FDBB5F' : '1px solid #DDD',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    position: 'relative',
                  }}
                >
                  {size === TRIAL_SIZE && (
                    <Chip
                      label="Essai"
                      size="small"
                      sx={{ position: 'absolute', top: 6, right: 6, height: 18, fontSize: 10, backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 700 }}
                    />
                  )}
                  <Typography sx={{ fontWeight: 800, fontSize: 20, mt: size === TRIAL_SIZE ? 1.5 : 0 }}>{size}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#666' }}>RDV</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
          <TextField
            label="Ou saisissez une quantité (min. 10)"
            type="number"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ min: 10 }}
          />

          <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 3, mb: 1 }}>Moyen de paiement</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Card
                onClick={() => !isTrial && setPaymentMethod('qonto_transfer')}
                sx={{
                  cursor: isTrial ? 'not-allowed' : 'pointer',
                  opacity: isTrial ? 0.45 : 1,
                  border: effectivePaymentMethod === 'qonto_transfer' ? '2px solid #FDBB5F' : '1px solid #DDD',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>🏦 Virement</Typography>
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  {isTrial ? 'Indisponible pour le pack d’essai' : 'Facture émise, activation après réception'}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                onClick={() => setPaymentMethod('stripe_card')}
                sx={{
                  cursor: 'pointer',
                  border: effectivePaymentMethod === 'stripe_card' ? '2px solid #FDBB5F' : '1px solid #DDD',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>💳 Carte bancaire</Typography>
                <Typography sx={{ fontSize: 12, color: '#666' }}>Paiement immédiat, activation directe</Typography>
              </Card>
            </Grid>
          </Grid>

          {displaySize >= TRIAL_SIZE && (
            <Paper sx={{ mt: 2, p: 2, backgroundColor: '#FFF8E1', borderRadius: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>
                Total : {totalHt.toLocaleString('fr-FR')}€ HT
              </Typography>
              <Typography variant="body2" sx={{ color: '#464646', mt: 0.5 }}>
                {displaySize} RDV × {UNIT_PRICE_HT}€ HT — {totalTtc.toLocaleString('fr-FR')}€ TTC
              </Typography>
            </Paper>
          )}

          {error && <Typography sx={{ color: '#C62828', fontSize: 14, mt: 2 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetDialog} sx={{ color: '#464646', textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={handleOrder}
            disabled={orderPack.isPending}
            sx={{
              backgroundColor: '#FDBB5F',
              color: '#262626',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#fcae4d' },
              '&:disabled': { opacity: 0.5 },
            }}
          >
            {orderPack.isPending ? 'Commande…' : effectivePaymentMethod === 'stripe_card' ? 'Payer par carte' : 'Commander'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation virement Qonto avec RIB */}
      <Dialog open={qontoConfirmation !== null} onClose={() => setQontoConfirmation(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Commande enregistrée 🏦</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#262626', mb: 2 }}>
            Votre facture {qontoConfirmation?.invoiceNumber ? <strong>n° {qontoConfirmation.invoiceNumber}</strong> : ''} a
            été émise et vous est envoyée par email. Effectuez le virement aux coordonnées ci-dessous : votre pack sera
            activé automatiquement dès réception.
          </Typography>
          {qontoConfirmation?.bankTransfer && (
            <BankTransferDetails info={qontoConfirmation.bankTransfer} amountTtc={qontoConfirmation.amountTtc} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setQontoConfirmation(null)}
            variant="contained"
            sx={{ backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 700, '&:hover': { backgroundColor: '#fcae4d' } }}
          >
            Compris
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paiement CB embarqué (Stripe Embedded Checkout — aucune redirection externe) */}
      <Dialog open={embed !== null} onClose={() => setEmbed(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Paiement par carte</DialogTitle>
        <DialogContent>
          {embed && (
            <EmbeddedCheckoutProvider
              stripe={embed.stripe}
              options={{ clientSecret: embed.clientSecret, onComplete: handleEmbedComplete }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEmbed(null)} sx={{ color: '#666' }}>
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Résultat du paiement CB (au retour de l'Embedded Checkout) */}
      <Dialog open={paymentResult !== null} onClose={() => setPaymentResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {paymentResult === 'success' ? 'Paiement réussi 🎉' : 'Paiement non finalisé'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#262626' }}>
            {paymentResult === 'success'
              ? 'Votre pack est payé et activé. Vous allez commencer à recevoir des RDV qualifiés.'
              : "Le paiement n'a pas été confirmé. Si vous avez été débité, contactez Skipcar."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setPaymentResult(null)}
            variant="contained"
            sx={{ backgroundColor: '#FDBB5F', color: '#262626', fontWeight: 700, '&:hover': { backgroundColor: '#fcae4d' } }}
          >
            Compris
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Packs;
